// lib/usePermission.ts
import { useMemo } from "react";
import { useUser } from "./UserContext";
import { useStaff } from "./StaffContext";
import { computePermissions } from "./permissions";
import type { Permission } from "./permissions";

export function usePermission(permission: Permission): boolean {
  const user = useUser() ?? null;
  const { activeStaff } = useStaff();
  return useMemo(
    () => computePermissions(user, activeStaff).has(permission),
    [user, activeStaff, permission]
  );
}

export function useHasAnyPermission(...permissions: Permission[]): boolean {
  const user = useUser() ?? null;
  const { activeStaff } = useStaff();
  return useMemo(() => {
    const set = computePermissions(user, activeStaff);
    return permissions.some((p) => set.has(p));
  }, [user, activeStaff, permissions]);
}

/** Returns the full computed permission set for the current session. */
export function usePermissions(): Set<Permission> {
  const user = useUser() ?? null;
  const { activeStaff } = useStaff();
  return useMemo(
    () => computePermissions(user, activeStaff),
    [user, activeStaff]
  );
}
